import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Dropdown,
    Label,
    ListBox,
    Pagination,
} from '@heroui/react';
import { DataGrid, InlineSelect, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BranchEnrollCell } from '../branchEnrollment';
import {
    BRANCH_COLUMN,
    branchRowActionsFor,
    BRANCHES_PAGE_SIZE_OPTIONS,
    NO_IMAGE_ICON,
    formatArea,
    formatInventoryValue,
    getPaginationWindow,
    type Branch,
    type BranchRowAction,
} from '../branchesMeta';

type BranchTableProps = {
    /** Whether the row menu offers Import products for this branch. */
    canImportProducts: (branch: Branch) => boolean;
    data: Branch[];
    onDeleteBranch: (id: string) => void;
    onImportProducts: (id: string) => void;
    onRequestArchive: (id: string) => void;
    onSetArchived: (id: string, archived: boolean) => void;
    onEditBranch: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

/**
 * Render-only branches grid. The data is **server-paginated** — `data` is
 * already the current page in the server's order (main branch first, then by
 * name), so there's no client-side sort or slice here; the footer just drives
 * the page / page-size controls. Columns aren't sortable because the API has no
 * sort parameter.
 */
export const BranchTable: React.FC<BranchTableProps> = ({
    canImportProducts,
    data,
    onDeleteBranch,
    onImportProducts,
    onRequestArchive,
    onSetArchived,
    onEditBranch,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tableAria: t('branches.components.branchTable.tableAria'),
            actionsAria: t('branches.components.branchTable.actionsAria'),
            previous: t('branches.components.branchTable.pagination.previous'),
            next: t('branches.components.branchTable.pagination.next'),
            perPage: t('branches.components.branchTable.pagination.perPage'),
            columns: {
                image: t('branches.components.branchTable.columns.image'),
                name: t('branches.components.branchTable.columns.name'),
                address: t('branches.components.branchTable.columns.address'),
                area: t('branches.components.branchTable.columns.area'),
                isMain: t('branches.components.branchTable.columns.isMain'),
                skuCount: t('branches.components.branchTable.columns.skuCount'),
                totalUnits: t(
                    'branches.components.branchTable.columns.totalUnits',
                ),
                inventoryValue: t(
                    'branches.components.branchTable.columns.inventoryValue',
                ),
                apiToken: t('branches.components.branchTable.columns.apiToken'),
            },
            empty: {
                title: t('branches.components.branchTable.empty.title'),
            },
            mainBadge: t('branches.components.branchTable.mainBadge'),
            archived: t('branches.components.branchTable.archived'),
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: BranchRowAction) => {
            switch (action) {
                case 'edit':
                    onEditBranch(id);
                    return;
                case 'import':
                    onImportProducts(id);
                    return;
                case 'archive':
                    onRequestArchive(id);
                    return;
                case 'unarchive':
                    onSetArchived(id, false);
                    return;
                default:
                    onDeleteBranch(id);
            }
        },
        [
            onDeleteBranch,
            onEditBranch,
            onImportProducts,
            onRequestArchive,
            onSetArchived,
        ],
    );

    const columns = React.useMemo<DataGridColumn<Branch>[]>(
        () => [
            {
                id: BRANCH_COLUMN.image,
                header: copy.columns.image,
                width: 72,
                cell: (item) =>
                    item.imageUrl ? (
                        <img
                            alt={item.name}
                            className="size-10 rounded-[8px] object-cover"
                            src={item.imageUrl}
                            {...getNodeTestIdProp(
                                `branch-row-image-${item.id}`,
                            )}
                        />
                    ) : (
                        <div
                            aria-hidden
                            className="flex size-10 items-center justify-center rounded-[8px] bg-surface-secondary text-muted"
                            {...getNodeTestIdProp(
                                `branch-row-image-fallback-${item.id}`,
                            )}
                        >
                            <Icon className="size-5" icon={NO_IMAGE_ICON} />
                        </div>
                    ),
            },
            {
                id: BRANCH_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                cell: (item) => (
                    <span className="flex items-center gap-2">
                        <span
                            className="text-sm font-medium text-foreground"
                            {...getNodeTestIdProp(`branch-row-name-${item.id}`)}
                        >
                            {item.name}
                        </span>
                        {item.archived ? (
                            <Chip
                                color="default"
                                size="sm"
                                {...getNodeTestIdProp(
                                    `branch-row-archived-${item.id}`,
                                )}
                            >
                                {copy.archived}
                            </Chip>
                        ) : null}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.address,
                header: copy.columns.address,
                accessorKey: 'address',
                cell: (item) => (
                    <span
                        className="text-sm text-foreground"
                        {...getNodeTestIdProp(`branch-row-address-${item.id}`)}
                    >
                        {item.address}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.area,
                header: copy.columns.area,
                accessorKey: 'area',
                width: 120,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(`branch-row-area-${item.id}`)}
                    >
                        {formatArea(item.area)}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.isMain,
                header: copy.columns.isMain,
                accessorKey: 'isMain',
                width: 110,
                align: 'center',
                cell: (item) =>
                    item.isMain ? (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-xs font-medium text-success-soft-foreground"
                            {...getNodeTestIdProp(
                                `branch-row-main-badge-${item.id}`,
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-3"
                                icon="gravity-ui:star-fill"
                            />
                            {copy.mainBadge}
                        </span>
                    ) : (
                        <span
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(`branch-row-main-${item.id}`)}
                        >
                            —
                        </span>
                    ),
            },
            {
                id: BRANCH_COLUMN.skuCount,
                header: copy.columns.skuCount,
                accessorKey: 'skuCount',
                width: 110,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `branch-row-sku-count-${item.id}`,
                        )}
                    >
                        {item.skuCount}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.totalUnits,
                header: copy.columns.totalUnits,
                accessorKey: 'totalUnits',
                width: 120,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `branch-row-total-units-${item.id}`,
                        )}
                    >
                        {item.totalUnits}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.inventoryValue,
                header: copy.columns.inventoryValue,
                accessorKey: 'inventoryValue',
                width: 160,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm font-medium tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `branch-row-inventory-value-${item.id}`,
                        )}
                    >
                        {formatInventoryValue(item.inventoryValueTetri)}
                    </span>
                ),
            },
            {
                id: BRANCH_COLUMN.apiToken,
                header: copy.columns.apiToken,
                width: 220,
                align: 'center',
                cell: (item) => <BranchEnrollCell branch={item} />,
            },
            {
                id: BRANCH_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(`branch-row-actions-${item.id}`)}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `branch-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as BranchRowAction,
                                        )
                                    }
                                >
                                    {branchRowActionsFor(
                                        item.archived,
                                        canImportProducts(item),
                                    ).map((action) => (
                                        <Dropdown.Item
                                            id={action.id}
                                            key={action.id}
                                            textValue={t(action.labelKey)}
                                            variant={action.variant}
                                            {...getNodeTestIdProp(
                                                `branch-row-action-${action.id}-${item.id}`,
                                            )}
                                        >
                                            <Icon icon={action.icon} />
                                            <Label>{t(action.labelKey)}</Label>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [canImportProducts, copy, onRowAction, t],
    );

    const pageWindow = getPaginationWindow(totalPages, page);

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('branch-table-widget')}
        >
            <DataGrid<Branch>
                aria-label={copy.tableAria}
                columns={columns}
                data={data}
                getRowId={(item) => item.id}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('branch-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp('branch-table-empty-title')}
                        >
                            {copy.empty.title}
                        </span>
                    </div>
                )}
                variant="primary"
                {...getNodeTestIdProp('branch-table-grid')}
            />
            {totalCount > BRANCHES_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('branch-table-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onPageSizeChange(Number(value))
                            }
                            value={String(pageSize)}
                            {...getNodeTestIdProp('branch-table-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {BRANCHES_PAGE_SIZE_OPTIONS.map((size) => (
                                        <ListBox.Item
                                            id={String(size)}
                                            key={size}
                                            textValue={String(size)}
                                        >
                                            {size}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ))}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`branch-table-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `branch-table-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
